import {
  BadgeDollarSign,
  HelpCircle,
  Images,
  Layout,
  Mail,
  MapPin,
  MessageSquareQuote,
  PanelBottom,
  ShieldCheck,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import type { Block, BlockType } from '@pageboss/shared';
import type { SectionOnWire } from '@/lib/types';
import { InspectorPanel } from './inspector-panel';
import { InspectorHeader } from './inspector-header';
import { ServicesInspector } from './services-inspector';
import { CredentialsInspector } from './credentials-inspector';
import { PricingInspector } from './pricing-inspector';
import { GalleryInspector } from './gallery-inspector';
import { TestimonialsInspector } from './testimonials-inspector';
import { TeamInspector } from './team-inspector';
import { ContactInspector } from './contact-inspector';
import { FaqInspector } from './faq-inspector';
import { HeroInspector } from './hero-inspector';
import { FooterInspector } from './footer-inspector';
import { LocationsInspector } from './locations-inspector';

// =====================================================================
// InspectorHost — finds the selected block in the page's sections,
// renders the correct inspector form for its type, wires onChange back
// up to the autosave hook. Mounted in PageEditLoaded as a sibling of
// the canvas; the parent flex row decides whether it docks or floats.
//
// Sections are passed as SectionOnWire (loose-typed); the selected
// block is cast to Block at the inspector boundary, matching the
// pattern in section-shell.tsx where preview rendering does the same.
// =====================================================================

interface SelectedBlockLocation {
  block: Block;
  sectionIndex: number;
  columnIndex: number;
  sectionId: string;
}

function locateSelectedBlock(
  sections: SectionOnWire[],
  blockId: string | null,
): SelectedBlockLocation | null {
  if (!blockId) return null;
  for (let s = 0; s < sections.length; s++) {
    const section = sections[s];
    if (!section) continue;
    for (let c = 0; c < section.columns.length; c++) {
      const col = section.columns[c];
      if (!col) continue;
      for (const block of col) {
        if (block.id === blockId) {
          return {
            block: block as unknown as Block,
            sectionIndex: s,
            columnIndex: c,
            sectionId: section.id,
          };
        }
      }
    }
  }
  return null;
}

const BLOCK_HEADER_INFO: Partial<Record<BlockType, { label: string; icon: LucideIcon }>> = {
  services: { label: 'Services block', icon: Wrench },
  credentials: { label: 'Trust strip', icon: ShieldCheck },
  pricing: { label: 'Pricing table', icon: BadgeDollarSign },
  gallery: { label: 'Gallery', icon: Images },
  testimonials: { label: 'Testimonials', icon: MessageSquareQuote },
  team: { label: 'Team', icon: Users },
  contact: { label: 'Contact', icon: Mail },
  faq: { label: 'FAQ', icon: HelpCircle },
  hero: { label: 'Hero', icon: Layout },
  footer: { label: 'Footer', icon: PanelBottom },
  locations: { label: 'Locations', icon: MapPin },
};

interface InspectorHostProps {
  sections: SectionOnWire[];
  selectedBlockId: string | null;
  onClose: () => void;
  onChangeBlock: (
    sectionId: string,
    columnIndex: number,
    blockId: string,
    nextProps: Record<string, unknown>,
  ) => void;
}

export function InspectorHost({
  sections,
  selectedBlockId,
  onClose,
  onChangeBlock,
}: InspectorHostProps) {
  const located = locateSelectedBlock(sections, selectedBlockId);
  if (!located) return null;
  const headerInfo = BLOCK_HEADER_INFO[located.block.type];
  // Only complex blocks have a configured inspector. Simple blocks (and
  // blocks we haven't wired yet in 3B-3d.a) silently keep the panel
  // closed.
  if (!headerInfo) return null;

  const breadcrumb = `Section ${located.sectionIndex + 1} · Column ${located.columnIndex + 1}`;

  return (
    <InspectorPanel open onClose={onClose}>
      <InspectorHeader
        icon={headerInfo.icon}
        title={headerInfo.label}
        breadcrumb={breadcrumb}
        onClose={onClose}
      />
      <div className="flex-1 overflow-y-auto">
        {located.block.type === 'services' ? (
          <ServicesInspector
            block={located.block}
            onChange={(next) =>
              onChangeBlock(located.sectionId, located.columnIndex, located.block.id, next)
            }
          />
        ) : null}
        {located.block.type === 'credentials' ? (
          <CredentialsInspector
            block={located.block}
            onChange={(next) =>
              onChangeBlock(located.sectionId, located.columnIndex, located.block.id, next)
            }
          />
        ) : null}
        {located.block.type === 'pricing' ? (
          <PricingInspector
            block={located.block}
            onChange={(next) =>
              onChangeBlock(located.sectionId, located.columnIndex, located.block.id, next)
            }
          />
        ) : null}
        {located.block.type === 'gallery' ? (
          <GalleryInspector
            block={located.block}
            onChange={(next) =>
              onChangeBlock(located.sectionId, located.columnIndex, located.block.id, next)
            }
          />
        ) : null}
        {located.block.type === 'testimonials' ? (
          <TestimonialsInspector
            block={located.block}
            onChange={(next) =>
              onChangeBlock(located.sectionId, located.columnIndex, located.block.id, next)
            }
          />
        ) : null}
        {located.block.type === 'team' ? (
          <TeamInspector
            block={located.block}
            onChange={(next) =>
              onChangeBlock(located.sectionId, located.columnIndex, located.block.id, next)
            }
          />
        ) : null}
        {located.block.type === 'contact' ? (
          <ContactInspector
            block={located.block}
            onChange={(next) =>
              onChangeBlock(located.sectionId, located.columnIndex, located.block.id, next)
            }
          />
        ) : null}
        {located.block.type === 'faq' ? (
          <FaqInspector
            block={located.block}
            onChange={(next) =>
              onChangeBlock(located.sectionId, located.columnIndex, located.block.id, next)
            }
          />
        ) : null}
        {located.block.type === 'hero' ? (
          <HeroInspector
            block={located.block}
            onChange={(next) =>
              onChangeBlock(located.sectionId, located.columnIndex, located.block.id, next)
            }
          />
        ) : null}
        {located.block.type === 'locations' ? (
          <LocationsInspector
            block={located.block}
            onChange={(next) =>
              onChangeBlock(located.sectionId, located.columnIndex, located.block.id, next)
            }
          />
        ) : null}
        {located.block.type === 'footer' ? (
          <FooterInspector
            block={located.block}
            onChange={(next) =>
              onChangeBlock(located.sectionId, located.columnIndex, located.block.id, next)
            }
          />
        ) : null}
      </div>
    </InspectorPanel>
  );
}
